import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, Repeat2, Heart, Kanban, Bookmark, Upload } from "lucide-react";
import ImageView from "../ImageView";
import CanvaView from "../CanvaView";
import { getCurrentTwitterMe } from "../../../api/AppLinkApi";
import { getBaseUrl } from "../../../api/api";
import { timeAgoShort } from "../../helpers";

interface Props {
  postType: string;
  content: string;
  scheduledDateTime: Date;
  mediaId: string;
  mediaType: "HOST" | "CANVA";
  engagement: any;
}

export default function TwitterPost({
  postType,
  content,
  scheduledDateTime,
  mediaId,
  mediaType,
}: Props) {
  const { t } = useTranslation();

  const [displayName, setDisplayName] = useState(t("Anonymous"));
  const [avatarUrl, setAvatarUrl] = useState();

  useEffect(() => {
    loadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfile = () => {
    getCurrentTwitterMe().then((data) => {
      setDisplayName(data?.name || t("Anonymous"));
      setAvatarUrl(data?.profile_image_url);
    });
  };

  return (
    <article className="max-w-2xl w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-sm">
      <header className="flex gap-3">
        {avatarUrl ? (
          <img src={avatarUrl} alt="avatar" className="w-12 h-12 rounded-full" />
        ) : (
          <div className="w-12 h-12 rounded-full">
            <svg className="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm">
                {displayName}
              </h3>
              <span className="text-slate-500 dark:text-slate-400 text-sm">
                @{(displayName || "anonymous").replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}
              </span>
              <span className="text-slate-400 dark:text-slate-500 text-sm">·</span>
              <time className="text-slate-400 dark:text-slate-500 text-sm">
                {timeAgoShort(scheduledDateTime)}
              </time>
            </div>
            <button className="text-gray-600 text-xl">⋯</button>
          </div>
          <p className="mt-2 text-slate-800 dark:text-slate-200 text-base leading-6 whitespace-pre-wrap">
            {content}
          </p>
          {/* optional media */}
          {postType === "image" ? (
            <div className="mt-3 rounded-xl overflow-hidden border border-gray-200">
              {mediaId ? (
                <>
                  {mediaType === "CANVA" ? (
                    <CanvaView fileId={mediaId} className="w-full h-full object-contain" />
                  ) : (
                    <ImageView fileName={mediaId} className="w-full h-full object-contain" />
                  )}
                </>
              ) : (
                <img
                  src={`${getBaseUrl()}/static/images/upload-images.png`}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          ) : null}
          {/* action row */}
          <div className="mt-3 flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
            <button
              className="flex items-center gap-2 hover:text-sky-500 focus:outline-none"
              type="button"
              title={t("Reply")}
            >
              <MessageCircle size={18} />
              <span>{0}</span>
            </button>
            <button
              className="flex items-center gap-2 hover:text-sky-500 focus:outline-none"
              type="button"
              title={t("Repost")}
            >
              <Repeat2 size={18} />
              <span>{0}</span>
            </button>
            <button
              className="flex items-center gap-2 hover:text-sky-500 focus:outline-none"
              type="button"
              title={t("Like")}
            >
              <Heart size={18} />
              <span>{0}</span>
            </button>
            <button
              className="flex items-center gap-2 hover:text-sky-500 focus:outline-none"
              type="button"
              title={t("View")}
            >
              <Kanban size={18} />
              <span>{0}</span>
            </button>
            <button
              className="flex items-center gap-2 hover:text-slate-600 focus:outline-none"
              type="button"
            >
              <Bookmark size={18} />
              <Upload size={18} />
            </button>
          </div>
        </div>
      </header>
    </article>
  );
}
